import React, { useContext, useEffect, useState } from 'react';
import { AirDropContext } from '../../../../utils/AirDropProvider';
import './Footer.scss';
import useDefaultHomeCountryCode from '../../../hooks/useDefaultHomeCountryCode';
import useLocale from '../../../hooks/useLocale';
import useTranslation from '../../../hooks/useTranslation';
import CookiePreferencesFooterLink from '../../CookiePreferencesFooterLink/CookiePreferencesFooterLink';

interface FooterLink {
  label: string;
  url: string;
}

function Footer() {
  const config: any = useContext(AirDropContext);
  const { defaultLocale, footerLinks, footerLinksByCountry, locale, loginRootUrl, showFooter } = config;
  const currentYear = new Date().getFullYear();
  const { getLocale } = useLocale();
  const { translateText } = useTranslation();
  const currentLanguage = getLocale();

  const LOGIN_PATH = /^(\/login\/static\/html)/; // start of path for relative urls in client config
  const LOGIN_BASEURI = `${loginRootUrl}/login/static/html`;
  const { getDefaultHomeCountryCode } = useDefaultHomeCountryCode();

  const [localeInitialized, setLocaleInitialized] = useState(false); // State to track i18n initialization

  useEffect(() => {
    const localeInitialization = async () => {
      getLocale();
      setLocaleInitialized(true);
    };
    localeInitialization();
  }, []);

  function getFooterLinks() {
    if (!localeInitialized) return [];
    const linksByCountry = footerLinksByCountry?.[getDefaultHomeCountryCode()];
    const formatLinks = (linksByCountry ?? footerLinks ?? []).map((link: any) => {
      const footerLink: FooterLink = { label: '', url: '' };
      footerLink.label = linksByCountry ? link?.defaultLabel : link?.label;
      footerLink.url = linksByCountry ? link?.defaultUrl : link?.url;
      if (linksByCountry) {
        const localizedUrl = link?.byLanguage?.[currentLanguage]?.url ? link?.byLanguage?.[currentLanguage]?.url : null;
        const localizedLabel = link?.byLanguage?.[currentLanguage]?.label ? link?.byLanguage?.[currentLanguage]?.label : null;
        footerLink.label = localizedLabel || link.defaultLabel;
        footerLink.url = localizedUrl || link.defaultUrl;
      } else if (locale !== defaultLocale) {
        const localizedUrl = link && link.urlsByLanguage && link.urlsByLanguage[locale] ? link.urlsByLanguage[locale] : null;
        footerLink.url = localizedUrl || link.url;
      }
      footerLink.url = footerLink?.url?.replace(LOGIN_PATH, LOGIN_BASEURI);
      footerLink.label = translateText(footerLink.label);
      return footerLink;
    });

    return formatLinks;
  }

  return (
    <div>
      {showFooter && (
        <footer className="progressive">
          <section>
            <div className="footer-container">
              <div className="legal-notification">
                <div className="legal-notification--links">
                  <ul className="legal-notification--links__list">
                    {getFooterLinks()?.map(({ label, url }: FooterLink) => (
                      <li key={label}>
                        <a href={url} target="_blank" rel="noreferrer" title={translateText('Opens in a new tab or window')}>
                          {label}
                        </a>
                      </li>
                    ))}
                    {config?.enableCookieConsent && <CookiePreferencesFooterLink /> }
                  </ul>
                </div>

                <div className="legal-notification--copy">
                  <div className="legal-notification--copy__content">
                    {`© 1996 – ${currentYear} `}
                    {translateText('Pearson. All rights reserved.')}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </footer>
      )}
    </div>
  );
}

export default React.memo(Footer);
